import { React, useEffect, useState } from "react";

import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import BuildIcon from "@mui/icons-material/Build";
import RefreshIcon from "@mui/icons-material/Refresh";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import settingLogo from "../assests/setting_icon.png";
import Logo from "../assests/logo.png";


// const HEALTH_URL = `${process.env.REACT_APP_API_URL?.replace("/api", "") || "http://localhost:5000"}/api/health`;
// const HEALTH_URL = `${process.env.REACT_APP_API_URL?.replace("/api", "/api/health")}`;

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";
const MAINTENANCE_PROBE_URL = API_BASE_URL.replace(/\/$/, "") + "/maintenance-status";
const POLL_INTERVAL_MS = 10_000;

export default function Maintenance() {
  const [checking, setChecking] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const checkMaintenanceStatus = async () => {
    setChecking(true);
    try {
      const res = await fetch(MAINTENANCE_PROBE_URL, { method: "GET" });
      const isStillInMaintenance = res.status === 503;

      if (!isStillInMaintenance) {
        localStorage.removeItem("maintenance");
        window.dispatchEvent(new Event("maintenanceChange"));
        return true;
      }
      return false;
    } catch {
      // Network errors are treated as still unavailable
      return false;
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    let timer;

    const poll = async () => {
      const isRecovered = await checkMaintenanceStatus();
      if (isRecovered) {
        window.location.assign("/");
        return;
      }
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    timer = setTimeout(poll, POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at 20% 20%, rgba(40,80,200,0.25), transparent 40%), radial-gradient(circle at 80% 0%, rgba(60,120,255,0.25), transparent 40%), linear-gradient(135deg, #020617, #020c2b 60%, #01030f)",
        color: "#fff",
        fontFamily: "'Montserrat', sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box sx={{ py: { xs: 2, sm: 3 }, px: { xs: 3, md: 5 } }}>
        <Container maxWidth="xl" disableGutters>
          <Box
            component="img"
            src={Logo}
            alt="Second Education"
            sx={{
              width: { xs: 140, sm: 160, md: 200 },
            }}
          />
        </Container>
      </Box>

      {/* Main */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          px: { xs: 3, sm: 4, md: 5 },
          py: { xs: 4, sm: 6, md: 0 },
        }}
      >
        <Container maxWidth="xl" disableGutters>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              justifyContent: "space-between",
              gap: { xs: 5, sm: 6, md: 10 },
            }}
          >
            {/* LEFT */}
            <Box
              sx={{
                flex: 1,
                maxWidth: { xs: "100%", md: 600 },
                textAlign: { xs: "center", md: "left" },
              }}
            >
              <Chip
                icon={<BuildIcon sx={{ fontSize: 14, color: "#FFF" }} />}
                label="SYSTEM OPTIMIZATION"
                sx={{
                  mb: 3,
                  px: 2,
                  height: 30,
                  background: "rgba(255,255,255,0.08)",
                  color: "#fff",
                  borderRadius: "50px",
                  fontSize: "10px",
                  fontWeight: 600,
                  letterSpacing: "1px",
                }}
              />

              {/* FIXED HEADING */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", md: "flex-start" },
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: {
                      xs: "28px",
                      sm: "36px",
                      md: "48px",
                      lg: "50px",
                    },
                    fontWeight: 800,
                    lineHeight: 1.15,
                    letterSpacing: "-0.02em",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontSize: "0.9em" }}>🚧</span>
                  We&apos;ll Be Back Soon
                </Typography>
              </Box>

              <Typography
                sx={{
                  color: "rgba(255,255,255,0.75)",
                  fontSize: { xs: "14px", sm: "15px", md: "16px" },
                  lineHeight: 1.7,
                  maxWidth: { xs: "100%", md: 480 },
                  mx: { xs: "auto", md: 0 },
                }}
              >
                We’re currently performing scheduled maintenance to improve your
                learning experience. Our engineers are fine-tuning the architecture
                to serve you better.
              </Typography>
            </Box>

            {/* RIGHT CARD */}
            <Paper
              elevation={0}
              sx={{
                width: { xs: "100%", sm: "90%", md: 420, lg: 480 },
                p: { xs: 3, sm: 4, md: 5 },
                borderRadius: "20px",
                background: "rgba(255,255,255,0.06)",
                backdropFilter: "blur(30px)",
                border: "1px solid rgba(255,255,255,0.2)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box textAlign="center">
                <Box
                  component="img"
                  src={settingLogo}
                  alt="settings"
                  sx={{
                    width: 60,
                    height: 60,
                    mb: 3,
                  }}
                />

                <Typography
                  sx={{
                    fontWeight: 700,
                    letterSpacing: "1.6px",
                    fontSize: "18px",
                    color: "#FFF",
                    mb: 2,
                  }}
                >
                  SCHEDULED MAINTENANCE
                </Typography>

                <Box
                  sx={{
                    width: 70,
                    height: 3,
                    background: "#4c6fff",
                    mx: "auto",
                    mb: 2,
                    borderRadius: "10px",
                  }}
                />

                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    fontFamily: "'Montserrat', sans-serif",
                    lineHeight: 1.5,
                    color: "#fff",
                    opacity: 0.8,
                    mb: 3,
                  }}
                >
                  Our system is temporarily unavailable while we perform important updates.
                </Typography>
              </Box>

              <Box
                sx={{
                  p: 3,
                  borderRadius: "14px",
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  mb: 4,
                }}
              >
                <Typography sx={{ fontSize: "12px", opacity: 0.7, fontWeight: "600", color: "#efefef" }}>
                  ESTIMATED UPTIME
                </Typography>

                <Typography
                  sx={{
                    mt: 1,
                    fontSize: { xs: "15px", sm: "16px" },
                    fontWeight: 600,
                    color: "white",
                    letterSpacing: "0.5px",
                  }}
                >
                  Expected Downtime: 30–60 Minutes
                </Typography>
              </Box>

              <Button
                fullWidth
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={async () => {
                  const isRecovered = await checkMaintenanceStatus();
                  if (isRecovered) {
                    window.location.assign("/");
                    return;
                  }
                  window.location.reload();
                }}
                disabled={checking}
                sx={{
                  height: "48px",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "15px",
                  letterSpacing: "0.5px",
                  fontFamily: "'Montserrat', sans-serif",
                  background: "linear-gradient(90deg, #4c6fff, #3b5bdb)",
                }}
              >
                {checking ? "Checking..." : "Refresh Page"}
              </Button>

              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 400,
                    letterSpacing: "0.5px",
                    opacity: 0.65,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.7,
                    color: "#FFF",
                  }}
                >
                  <AccessTimeIcon sx={{ fontSize: 16 }} />
                  Last Updated {isMobile ? "a few" : "6"} Minutes Ago
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          py: 3,
          px: { xs: 3, md: 5 },
          borderTop: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Container maxWidth="xl" disableGutters>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexDirection: { xs: "column", sm: "row" },
              textAlign: { xs: "center", sm: "left" },
              gap: 1.5,
            }}
          >
            <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
              © 2026 Second Education — All Rights Reserved.
            </Typography>

            <Typography
              component="a"
              href="mailto:Admissions@secondeducation.in"
              sx={{
                fontSize: "13px",
                fontWeight: 500,
                color: "#fff",
                textDecoration: "none",
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              Email: Admissions@secondeducation.in
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
